import { getHoursWeeksMonths } from 'pages/AffordaWagesAndIncome/submit/mapIncomeTotals/incomeTotals.utils';
import { booleanToYesNo, capitalizeFirstLetter } from 'utils';
import cloneDeep from 'lodash/cloneDeep';
import {
  INCOME_CHECKLIST_PAY_TYPES,
  PAY_FREQUENCY,
} from 'pages/AffordaWagesAndIncome/constants';
import { EMPLOYMENT_TYPE as EMPLOYMENT_TYPES } from 'pages/AffordaWagesAndIncome/constants.job';
import {
  DEFAULT_WAGES_AND_INCOME_STRUCTURE,
  OTHER_INCOME_TYPES,
} from './constants';
import {
  SOCIAL_SEC_SSDI,
  SOCIAL_SEC_SSI,
} from 'pages/AffordaWagesAndIncome/constants.socialSec';
import {
  SOURCE_DISABILITY,
  SOURCE_UNEMPLOYMENT,
} from 'pages/AffordaWagesAndIncome/constants.unemployment';
import { PENSION_SOURCES } from 'pages/AffordaWagesAndIncome/constants.pension';

/**
 * Calculates the total income from an array of income items
 * @param {Array} items - Array of income items with incomeTotal property
 * @returns {string} - Total income formatted as a string with 2 decimal places
 */
const determineIncome = (items) => {
  return items?.length > 0
    ? items.reduce((sum, item) => sum + item.incomeTotal, 0).toFixed(2)
    : '0.00';
};

/**
 * Helper function to process standard income fields
 * @param {Object} incomeChecklist - The income checklist object to update
 * @param {Array} items - Array of income items
 * @param {string} prefix - Prefix for the field names (e.g., 'disability', 'pension')
 * @param {string} [payFrequencyDefault=PAY_FREQUENCY.annually] - Default pay frequency
 * @returns {Object} - Updated incomeChecklist
 */
const processIncomeFields = (
  incomeChecklist,
  items,
  prefix,
  payFrequencyDefault = PAY_FREQUENCY.annually,
) => {
  const hasItems = items?.length > 0;

  // Set yes/no flag
  incomeChecklist[prefix] = booleanToYesNo(hasItems);

  // Set hours/weeks/months
  incomeChecklist[`${prefix}HoursWeeksMonths`] = getHoursWeeksMonths(items);

  // Set income amount
  incomeChecklist[`${prefix}Income`] = determineIncome(items);

  // Set pay frequency
  const payFrequencyField = prefix.endsWith('Benefits')
    ? `${prefix}PayFrequency`
    : `${prefix}PayFrequency`;
  incomeChecklist[payFrequencyField] =
    hasItems && items.length === 1
      ? items[0].payFrequency
      : payFrequencyDefault;

  // Set pay rate
  const payRateField = prefix.endsWith('Benefits')
    ? `${prefix}PayRate`
    : `${prefix}PayRate`;
  incomeChecklist[payRateField] =
    hasItems && items.length === 1
      ? items[0].payRate.toString()
      : incomeChecklist[`${prefix}Income`];

  return incomeChecklist;
};

/**
 * Helper function to add items to the otherList
 * @param {Object} incomeChecklist - The income checklist object to update
 * @param {Array} items - Array of income items to add to otherList
 * @param {string} incomeName - Name to use for the income
 * @param {string} incomeType - Income type ID
 * @returns {Object} - Updated incomeChecklist
 */
const addToOtherList = (
  incomeChecklist,
  items,
  incomeType = OTHER_INCOME_TYPES.nonSpecified,
  incomeName = null,
) => {
  if (!items?.length) return incomeChecklist;

  incomeChecklist.other = 'yes';

  items.forEach((item) => {
    incomeChecklist.otherList.push({
      IncomeName: incomeName ?? capitalizeFirstLetter(item.payType) ?? '',
      PayRate: item.payRate?.toString() || '0',
      HoursWeeksMonths: item.HoursWeekMonth || '0',
      PayFrequency: item.payFrequency || PAY_FREQUENCY.annually,
      Income: item.incomeTotal?.toFixed(2) || '0.00',
      IncomeType: incomeType,
    });
  });

  incomeChecklist.otherIncome = determineIncome(items);

  return incomeChecklist;
};

const addToEmploymentList = (incomeChecklist, items, payType = null) => {
  if (!items.length) return incomeChecklist;

  items.forEach((item) => {
    incomeChecklist.employmentList.push({
      payType: payType ?? item.payType,
      PayRate: item.payRate.toString(),
      PayFrequency: item.payFrequency,
      Income: item.incomeTotal.toFixed(2),
      HoursWeeksMonths: item.HoursWeekMonth || 0,
    });
  });

  return incomeChecklist;
};

/**
 * Transforms member incomes into a specific structure for wages and income form
 * This is a simplified version showing the refactoring pattern
 * @param {Array} memberIncomes - Array of member income objects
 * @returns {Array} - Transformed income data
 */
const parseToWagesStructure = (memberIncomes) => {
  return memberIncomes.map((member) => {
    const {
      anticipatedJobs,
      jobs,
      alimonies,
      childSupports,
      gifts,
      onlines,
      otherIncomes,
      pensions,
      publicAids,
      socialSecs,
      trusts,
      unemployments,
    } = member;

    let incomeChecklist = cloneDeep(
      DEFAULT_WAGES_AND_INCOME_STRUCTURE.incomeChecklist,
    );

    // Jobs
    const allJobs = [...jobs, ...anticipatedJobs];
    const selfEmployedJobs = [];
    const militaryJobs = [];
    const regularJobs = [];

    jobs.forEach((job) => {
      if (job.employmentType === EMPLOYMENT_TYPES.selfEmployed) {
        selfEmployedJobs.push(job);
      } else if (job.employmentType === EMPLOYMENT_TYPES.military) {
        militaryJobs.push(job);
      } else {
        regularJobs.push(job);
      }
    });

    const hasJobs = allJobs?.length > 0;
    incomeChecklist.employment = booleanToYesNo(hasJobs);
    incomeChecklist.employmentIncome = determineIncome(jobs);
    incomeChecklist.notWorking = booleanToYesNo(!hasJobs);

    // Jobs
    addToEmploymentList(
      incomeChecklist,
      regularJobs,
      INCOME_CHECKLIST_PAY_TYPES.basePay,
    );

    // Anticipated Jobs
    addToEmploymentList(
      incomeChecklist,
      anticipatedJobs,
      INCOME_CHECKLIST_PAY_TYPES.basePay,
    );

    // Self Employment

    processIncomeFields(incomeChecklist, selfEmployedJobs, 'selfEmployment');

    // Military Pay
    processIncomeFields(incomeChecklist, militaryJobs, 'militaryPay');

    // Alimonies
    addToOtherList(
      incomeChecklist,
      alimonies,
      OTHER_INCOME_TYPES.nonSpecified,
      'Alimony',
    );

    // Child support

    // Process Child Support - Special case handling
    const arrayWithFirstChildSupport = childSupports?.slice(0, 1) || [];
    const firstChildSupport = arrayWithFirstChildSupport[0];
    const arrayWithRemainingChildSupports = childSupports?.slice(1) || [];
    const currentChildSupportsValues = member.childSupportValues?.[
      firstChildSupport?.id
    ] ?? { record: {}, incomeItem: {}, incomeChecklist: {} };

    // Add data for first child support
    incomeChecklist = {
      ...incomeChecklist,
      ...currentChildSupportsValues.incomeChecklist,
    };
    const courtOrderedIncomeItemArray = [
      currentChildSupportsValues.incomeItem.courtOrderedIncomeItem,
    ];

    if (currentChildSupportsValues.record.courtOrderedChildSupport === 'yes') {
      incomeChecklist.anticipateChildSupport = 'no';

      incomeChecklist.courtOrderedChildSupportHoursWeeksMonths =
        getHoursWeeksMonths(courtOrderedIncomeItemArray);
      incomeChecklist.courtOrderedChildSupportIncome = determineIncome(
        courtOrderedIncomeItemArray,
      );
    }

    if (currentChildSupportsValues.record.receivingOrdered === 'yes') {
      incomeChecklist.collectChildSupport = 'no';

      // Set receiving support to equal court ordered support
      incomeChecklist.receivingChildSupportPayFrequency =
        incomeChecklist.courtOrderedChildSupportPayFrequency;
      incomeChecklist.receivingChildSupportPayRate =
        incomeChecklist.courtOrderedChildSupportPayRate;
      incomeChecklist.receivingChildSupportHoursWeeksMonths =
        getHoursWeeksMonths(courtOrderedIncomeItemArray);
      incomeChecklist.receivingChildSupportIncome = determineIncome(
        courtOrderedIncomeItemArray,
      );
    }

    if (currentChildSupportsValues.record.receivingOrdered === 'no') {
      if (currentChildSupportsValues.record.receivingAny === 'yes') {
        incomeChecklist.receivingChildSupport = 'yes';

        const receivingIncomeItemArray = [
          currentChildSupportsValues.incomeItem.receivingIncomeItem,
        ];
        incomeChecklist.receivingChildSupportHoursWeeksMonths =
          getHoursWeeksMonths(receivingIncomeItemArray);
        incomeChecklist.receivingChildSupportIncome = determineIncome(
          receivingIncomeItemArray,
        );
      }
    }

    // Add remaining child supports to otherList
    if (arrayWithRemainingChildSupports.length > 0) {
      addToOtherList(
        incomeChecklist,
        arrayWithRemainingChildSupports,
        OTHER_INCOME_TYPES.nonSpecified,
        'Child Support',
      );
    }

    const PENSIONS = [
      // Pensions
      PENSION_SOURCES.DEATH,
      PENSION_SOURCES.ANNUITY,
      PENSION_SOURCES.INSURANCE,
    ];
    const regularPensions =
      pensions?.filter((p) => !PENSIONS.includes(p.source)) || [];
    const otherTypePensions =
      pensions?.filter((p) => PENSIONS.includes(p.source)) || [];

    regularPensions.forEach((pension) => {
      if (pension.source === PENSION_SOURCES.PENSION) {
        processIncomeFields(incomeChecklist, [pension], 'pension');
      }
      if (pension.source === PENSION_SOURCES.VA) {
        processIncomeFields(incomeChecklist, [pension], 'vaBenefits');
      }
      if (pension.source === PENSION_SOURCES.RETIREMENT) {
        processIncomeFields(incomeChecklist, [pension], 'retirement');
      }
    });

    addToOtherList(
      incomeChecklist,
      otherTypePensions,
      OTHER_INCOME_TYPES.nonSpecified,
    );

    // Public Aid/TANF
    processIncomeFields(incomeChecklist, publicAids, 'tanf');

    // Unemployment/Workers Comp/Disability

    // Unemployment/Workers Comp
    const unemploymentItems = unemployments?.filter(
      ({ source }) => source === SOURCE_UNEMPLOYMENT,
    );
    processIncomeFields(
      incomeChecklist,
      unemploymentItems,
      `${SOURCE_UNEMPLOYMENT}Benefits`,
    );

    // Disability
    const disabilityItems = unemployments?.filter(
      ({ source }) => source === SOURCE_DISABILITY,
    );
    processIncomeFields(incomeChecklist, disabilityItems, SOURCE_DISABILITY);

    // Gifts
    processIncomeFields(incomeChecklist, gifts, 'recurringGift');

    // Online Income
    processIncomeFields(incomeChecklist, onlines, 'incomeFromOnlineSources');

    // Supplemental Social Security
    const socialSecurityItems =
      socialSecs?.filter((s) => s.source === SOCIAL_SEC_SSI) || [];
    processIncomeFields(
      incomeChecklist,
      socialSecurityItems,
      'supplementalSecurity',
      PAY_FREQUENCY.monthly,
    );
    incomeChecklist.supplementalSecurityClaimNumber =
      socialSecurityItems[0]?.claimNumber ?? '';

    // Social Security Disability Insurance
    const socialSecurityDisabilityItems =
      socialSecs?.filter((s) => s.source === SOCIAL_SEC_SSDI) || [];
    processIncomeFields(
      incomeChecklist,
      socialSecurityDisabilityItems,
      'socialSecurity',
      PAY_FREQUENCY.monthly,
    );
    incomeChecklist.socialSecurityClaimNumber =
      socialSecurityDisabilityItems[0]?.claimNumber ?? '';

    // Trusts
    processIncomeFields(incomeChecklist, trusts, 'trust');

    // Other Income
    addToOtherList(incomeChecklist, otherIncomes);

    return {
      memberId: member.memberId,
      incomeChecklist,
      annualIncome: member.totalIncome.toFixed(2),
    };
  });
};

export default parseToWagesStructure;
